import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChainId, StoreDebtEventId, StoreDebtId } from "@KassaroAB/shared/types/ids";
import type { RestCreateStoreDebtSettlement } from "@KassaroAB/shared/types/storeDebt";
import {
  createStoreDebtSettlementApi,
  downloadStoreDebtReportPdfApi,
  getStoreDebtApi,
  getStoreDebtEventsApi,
  getStoreDebtsApi,
} from "../api/storeDebtsApi";

export const useGetStoreDebtsQuery = (chainId: ChainId | undefined) => {
  return useQuery({
    queryKey: ["chains", chainId, "storeDebts"],
    queryFn: () => getStoreDebtsApi(chainId!),
    enabled: !!chainId,
  });
};

export const useGetStoreDebtQuery = (
  chainId: ChainId | undefined,
  storeDebtId: StoreDebtId | null,
) => {
  return useQuery({
    queryKey: ["chains", chainId, "storeDebts", storeDebtId],
    queryFn: () => getStoreDebtApi(chainId!, storeDebtId!),
    enabled: !!chainId && !!storeDebtId,
  });
};

export const useGetStoreDebtEventsQuery = (
  chainId: ChainId | undefined,
  storeDebtId: StoreDebtId | null,
) => {
  return useInfiniteQuery({
    queryKey: ["chains", chainId, "storeDebts", storeDebtId, "events"],
    queryFn: ({ pageParam }: { pageParam: StoreDebtEventId | null }) =>
      getStoreDebtEventsApi(chainId!, storeDebtId!, pageParam),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 50) {
        return undefined;
      }
      return lastPage[lastPage.length - 1].id;
    },
    enabled: !!chainId && !!storeDebtId,
  });
};

export const useCreateStoreDebtSettlementMutation = (
  chainId: ChainId | undefined,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      storeDebtId,
      body,
    }: {
      storeDebtId: StoreDebtId;
      body: RestCreateStoreDebtSettlement;
    }) => createStoreDebtSettlementApi(chainId!, storeDebtId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chains", chainId, "storeDebts"],
      });
    },
  });
};

export const useDownloadStoreDebtReportPdfMutation = (
  chainId: ChainId | undefined,
) => {
  return useMutation({
    mutationFn: ({
      storeDebtId,
      startDate,
      endDate,
    }: {
      storeDebtId: StoreDebtId;
      startDate?: string;
      endDate?: string;
    }) =>
      downloadStoreDebtReportPdfApi(chainId!, storeDebtId, startDate, endDate),
  });
};
